import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { GetStorageToken, SetStorageToken } from "../util/base.util";
import { ToasterContext } from "./toaster.context";
import { IUser } from "../shared/type/user.type";

type IAuthContext = {
    token: string | undefined;
    user: IUser | undefined;
    setToken: (token: string | undefined) => void;
    setUser: (user: IUser) => void;
    logout: () => void;
    UpdateUserProperties: (data: Partial<{[key in keyof IUser]: any}>) => void;
};

export const AuthContext = createContext<IAuthContext>({
    token: undefined,
    user: undefined,
    setToken: () => {},
    setUser: () => {},
    logout: () => {},
    UpdateUserProperties: () => {},
});

export function AuthProvider({children}: {
    children: React.ReactNode;
}) {
    const toaster = useContext(ToasterContext);

    const [state, setState] = useState<{
        token: string|undefined;
        user: IUser|undefined;
    }>({
        token: GetStorageToken() || undefined,
        user: undefined
    });

    function setToken(token: string|undefined) {
        SetStorageToken(token);
        setState(prev => ({...prev, token}));
    }

    function setUser(user: IUser) {
        setState(prev => ({...prev, user}));
    }

    const UpdateUserProperties = useCallback((data: Partial<{[key in keyof IUser]: any}>) => {
        if (!state.user) return;

        const newUser = {...state.user};
        Object.assign(newUser, data);
        setState(prev => ({...prev, user: newUser}))
    }, [state.user]);

    function logout() {
        SetStorageToken();
        setState(prev => ({token: undefined, user: undefined}));
    }

    useEffect(() => {
        if (!state.token) return;

        fetch("/api/v1/auth/me", {
            headers: {authorization: `Bearer ${state.token}`}
        })
            .then(async res => {

                if (!res.ok) {
                    const data = await res.json();
                    toaster.makeToast(data.message);
                    logout();
                    return;
                }
                const data = await res.json();
                setUser(data);
            })
            .catch(err => {
                toaster.makeToast(`${err}`);
                logout();
            });
    }, [state.token]);

    return (
        <AuthContext.Provider value={{
            token: state.token,
            user: state.user,
            setToken,
            setUser,
            logout,
            UpdateUserProperties
        }}>
            {children}
        </AuthContext.Provider>
    );
}